/**
 * /product — Product overview.
 *
 * Real product breakdown for South African fiduciary firms: trust onboarding,
 * resolution signing (internal + external), DSAR fulfilment, FICA reporting,
 * distribution payments via Paystack, audit chain (WORM + hash-chain),
 * POPIA officer designation.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/product')({
  component: ProductPage,
});

function ProductPage() {
  useEffect(() => {
    document.title = 'Product · Elreign: trust administration for South African fiduciary firms';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'A purpose-built platform for South African trust administration. Onboarding, resolutions, DSAR fulfilment, FICA reporting, distributions, and an immutable audit chain, in one record.',
    );
  }, []);

  return (
    <MarketingLayout>
      {/* The `product-page` scoping class BEH-27 round 2 added here is gone
          (round 4): the base `.eyebrow` rule is muted ink on every route now,
          so this page needs no class of its own to avoid the brass. */}
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ textAlign: 'center', marginBottom: 72 }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>Product overview</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(40px, 6vw, 72px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.08,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            The full lifecycle of a trust, <em>on one record.</em>
          </h1>
          <p
            style={{
              fontSize: 20,
              color: 'var(--ink-3)',
              lineHeight: 1.6,
              maxWidth: 640,
              margin: '0 auto',
            }}
          >
            {/* HON-1. This read "a cryptographic chain that SATISFIES POPIA, FICA s23, and the
                Trust Property Control Act" until 2026-08-27. That is an absolute compliance
                assertion, and a stronger one than the three phrasings the guard was built to ban,
                which is why it survived: the guard knew the word "compliant" and this sentence
                never used it. It is also materially false today for the reason the guard's own
                comment gives, that FICA screening has no provider configured on production.
                Built for is a claim about design; satisfies is a claim about outcome. */}
            Onboarding through to distribution, signing through to FICA lodgement, the trust deed
            through to the last quarterly statement. Every action is sealed on a cryptographic
            chain, built for POPIA, FICA s23 and the Trust Property Control Act.
          </p>
        </header>

        {/* Capabilities */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 56 }}>
          <Capability
            number="01"
            eyebrow="Trust onboarding"
            heading="From deed to first resolution, in an afternoon."
            body={
              <>
                <p>
                  The onboarding wizard captures trust particulars (deed reference, Master&apos;s
                  registration number, jurisdiction), the trustee panel, the beneficiary register,
                  and the initial asset schedule. FICA documents are collected per beneficial owner
                  and screened in the same flow. Risk rating is computed against your firm&apos;s
                  policy thresholds, not a generic template.
                </p>
                <p>
                  The trust is provisioned with a tenant-scoped audit container, a document vault
                  with WORM immutability, and a signing channel for each trustee. The first
                  resolution can be drafted before the kettle boils.
                </p>
              </>
            }
            bullets={[
              'Deed parsing: clauses extracted into structured fields, originals retained',
              'Trustee KYC and FICA: collected once, reused across every action',
              'Beneficial owner register populated and submitted to the Master',
              'Risk rating per firm policy, with override audit trail',
            ]}
          />

          <Capability
            number="02"
            eyebrow="Resolution signing"
            heading="Internal signing. External signing. Same chain."
            body={
              <>
                <p>
                  Resolutions are drafted by a fiduciary administrator, reviewed by the trustees
                  named on the deed, and signed with a Face ID or device biometric prompt. Every
                  signature is preceded by a fingerprint sent out of band, typically an SMS to the
                  trustee&apos;s registered number, so a compromised inbox cannot produce a valid
                  signature.
                </p>
                <p>
                  External signers (auditors, witnesses, professional executors who are not Elreign
                  users) sign through a single-use, time-boxed link without creating an account.
                  Their identity is verified by a one-time code sent to their registered email
                  and/or mobile number before they can review and sign. Their signature is bound to
                  the same hash chain as every internal signature, with the same body-swap defence
                  re-deriving the resolution hash on the server.
                </p>
              </>
            }
            bullets={[
              'Body-swap defence: server re-derives the resolution hash on submit',
              'Out-of-band fingerprint via SMS or authenticator app',
              'External signers via single-use link with one-time-code verification',
              'Every signature linked to the preceding chain hash',
            ]}
          />

          <Capability
            number="03"
            eyebrow="Distribution payments"
            heading="Authorise. Pay. Reconcile. On record."
            body={
              <>
                <p>
                  Distribution payments are authorised by resolution, executed via Paystack against
                  the beneficiary&apos;s verified bank account, and reconciled back to the trust
                  ledger. The payment record carries the authorising resolution hash, the Paystack
                  transaction reference, and a receipt signed by both parties.
                </p>
                <p>
                  The full distribution history per beneficiary (across every trust they hold an
                  interest in) is available to the trustee, to the beneficiary themselves through
                  their own portal, and to the auditor on demand.
                </p>
              </>
            }
            bullets={[
              'Paystack-rails ZAR payments to verified beneficiary accounts',
              'Resolution hash embedded in every payment record',
              'Tax certificate produced automatically per distribution',
              'Beneficiary portal shows every distribution they have received',
            ]}
          />

          <Capability
            number="04"
            eyebrow="DSAR fulfilment"
            heading="Every data subject right, handled correctly."
            body={
              <>
                <p>
                  When a beneficiary, founder, or trustee exercises their POPIA right of access,
                  rectification, or erasure, the request lands in the firm&apos;s DSAR queue with
                  a 30-day clock and a structured fulfilment workflow. Export bundles include every
                  document, message, signature event, and audit entry concerning the subject.
                </p>
                <p>
                  Erasure honours the FICA s23 retention window: records that must be kept for
                  five years are not deleted, but the personal information layer can be erased, on
                  request, through an approval-gated, dual-control cryptographic key-destruction
                  procedure, with each step sealed on the audit chain. The audit chain itself
                  remains intact; when the protecting keys are destroyed, the personal information
                  embedded in it is rendered unrecoverable.
                </p>
              </>
            }
            bullets={[
              'Right of access: bundled export, signed receipt',
              'Right to rectify: with audit trail of the change',
              'Right to erasure: approval-gated cryptographic key destruction (on request)',
              'Statutory exceptions clearly flagged in the response',
            ]}
          />

          <Capability
            number="05"
            eyebrow="FICA reporting"
            heading="goAML XML, not a screenshot of a spreadsheet."
            body={
              <>
                <p>
                  Suspicious transaction reports (STRs) and threshold reports (CTRs) are produced
                  in goAML XML. The compliance officer designated under FICA s43B reviews and
                  authorises each submission under a four-eye rule, and the submission record is
                  sealed on the audit chain. Direct eFiling to the Financial Intelligence Centre
                  activates once the firm&apos;s goAML registration and credentials are configured;
                  until then the authorised report is filed locally and exported for lodgement
                  through the firm&apos;s existing goAML channel.
                </p>
                <p>
                  Beneficial ownership records are kept current under the Trust Property Control
                  Act as trustees and beneficiaries change, and the most recent BO register is held
                  ready for inspection at all times.
                </p>
              </>
            }
            bullets={[
              'goAML STR and CTR: direct eFiling activates once credentials are configured',
              'BO register kept current per the Trust Property Control Act',
              'Five-year audit retention per s23, WORM-locked',
              'MLRO designation and successor planning in product',
            ]}
          />

          <Capability
            number="06"
            eyebrow="Audit chain"
            heading="WORM-locked. Hash-chained. Signed on export."
            body={
              <>
                <p>
                  Every event on every trust (resolution drafted, fingerprint sent, signature
                  applied, document uploaded, distribution paid, beneficial owner updated) is
                  recorded with a SHA-256 content hash and a chain hash linking it to the
                  preceding record. The stream is written to Azure Blob Storage with WORM
                  immutability enforced at the container level.
                </p>
                {/* MKT-01 (register row, ruling 2026-09-03 "soften now").
                    This paragraph used to promise "a published Elreign verifier"
                    and a "public hash specification", and a court presentation
                    "without our cooperation". None of the three exists: the
                    signing key's public half is not published, there is no
                    verifier tool to download, and the hash format is not written
                    down anywhere a stranger can read. What DOES exist is the
                    signed export below (backend/src/functions/audit-export.ts:
                    detached PS256 JWS plus a manifest carrying the bundle's
                    SHA-256 and the signing key id). Say that, and say the rest
                    is not built yet, until it is. */}
                <p>
                  An export of the chain is a plain text bundle, signed with a detached
                  signature and accompanied by a manifest giving the bundle&rsquo;s SHA-256 and the
                  id of the key that signed it. Anyone holding the matching public key can
                  recheck every hash in the chain for themselves, on their own machine, without
                  running our software.
                </p>
                <p>
                  We do not yet publish that public key, a verifier you can download, or the hash
                  format as a written specification. Today an independent check means asking us
                  for the key and the manifest, and both are yours on request.
                </p>
              </>
            }
            bullets={[
              'Per-event SHA-256 content hash, plus chain hash',
              'WORM-locked storage at container level',
              'Exports are signed, with the bundle hash and signing key id in the manifest',
              'Five-year minimum retention, with longer windows on request',
            ]}
          />

          <Capability
            number="07"
            eyebrow="POPIA officer designation"
            heading="Information Officer designation, in product."
            body={
              <>
                <p>
                  The firm&apos;s designated Information Officer and any Deputy Information
                  Officers are recorded in product, with the Information Regulator registration
                  submission prepared for filing. The PIA (Personal Information Impact Assessment), the PAIA manual,
                  and the breach notification workflow are pre-templated against the South African
                  regulatory regime and amended in product as the firm grows.
                </p>
                <p>
                  When a breach is detected, the 72-hour notification clock starts automatically;
                  the regulator notification and affected-party notifications are drafted from real
                  data in the audit chain rather than from scratch.
                </p>
              </>
            }
            bullets={[
              'IO and Deputy IO registration in product',
              'PIA and PAIA manual templates per s17 / s18',
              'Breach notification with 72-hour automatic clock',
              'Annual compliance calendar with prompts ahead of deadlines',
            ]}
          />
        </section>

        {/* CTA */}
        <section
          style={{
            marginTop: 96,
            padding: '48px 40px',
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 'var(--r-xl)',
            textAlign: 'center',
          }}
        >
          <p className="eyebrow" style={{ marginBottom: 16 }}>Ready to evaluate?</p>
          <h2
            className="serif"
            style={{
              fontSize: 'clamp(28px, 4vw, 40px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              margin: '0 0 16px',
              lineHeight: 1.2,
              letterSpacing: '-0.015em',
            }}
          >
            Walk through your first trust with us.
          </h2>
          <p style={{ fontSize: 17, color: 'var(--ink-3)', lineHeight: 1.6, margin: '0 auto 28px', maxWidth: 560 }}>
            A thirty-minute call. We onboard a real trust together. You see the chain, the signing
            flow, the FICA report, and the distribution rails. No slides.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/contact"
              style={{
                background: 'var(--accent-400)',
                color: 'var(--primary-900)',
                padding: '12px 24px',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Book a walkthrough
            </Link>
            <Link
              to="/pricing"
              style={{
                fontSize: 15,
                color: 'var(--primary-600)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(31,77,69,0.25)',
                paddingBottom: 1,
                alignSelf: 'center',
              }}
            >
              See pricing
            </Link>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

/* ── Capability block ──────────────────────────────────────────────────────── */

interface CapabilityProps {
  number: string;
  eyebrow: string;
  heading: string;
  body: React.ReactNode;
  bullets: string[];
}

function Capability({ number, eyebrow, heading, body, bullets }: CapabilityProps) {
  return (
    <article
      // The label column sits beside the copy on a desk and stacks on a phone
      // (design gate F1); the grid lives in design-system.css.
      className="product-capability"
      style={{
        paddingTop: 32,
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <div>
        <div
          className="mono"
          style={{
            fontSize: 13,
            color: 'var(--accent-600)',
            letterSpacing: '0.04em',
          }}
        >
          {number}
        </div>
        <p className="eyebrow" style={{ marginTop: 12 }}>
          {eyebrow}
        </p>
      </div>
      <div>
        <h2
          className="serif"
          style={{
            fontSize: 'clamp(24px, 3vw, 32px)',
            fontStyle: 'italic',
            fontWeight: 300,
            color: 'var(--ink)',
            margin: '0 0 20px',
            lineHeight: 1.2,
            letterSpacing: '-0.015em',
          }}
        >
          {heading}
        </h2>
        <div
          style={{
            fontSize: 17,
            color: 'var(--ink-2)',
            lineHeight: 1.65,
            marginBottom: 24,
          }}
        >
          {body}
        </div>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {bullets.map((b) => (
            <li
              key={b}
              style={{
                fontSize: 14,
                color: 'var(--ink-2)',
                paddingLeft: 18,
                position: 'relative',
                lineHeight: 1.5,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '0.55em',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--accent-400)',
                }}
              />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
